/* eslint-disable no-param-reassign */
import type { IAttributes, IAugmentedJQuery, IComponentController, IScope, ITimeoutService } from "angular";
import { forEach } from "angular";
import { finalize } from "rxjs/operators";
import type { Subscription } from "rxjs";
import { zip as observableZip } from "rxjs";
import { cloneDeep, isEmpty } from "lodash";
import "@progress/kendo-ui/js/kendo.core";

import type { ICoverageCalculationsService } from "App/Modules/Gui";
import { HumanSizeService } from "App/Modules/Gui/Services";
import type {
    ISiteEquipmentRepository,
    ISiteFunctionalitiesRepository,
    ISitesRepository,
} from "App/Modules/Repositories";
import type { IDefaultNamingService, IFormContainerResolve, IFeatureFlagService } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";

import type {
    IAlarmZone,
    IConfigurationGroupDetection,
    IFunctionalityMotionDetection,
    IMap,
    IPoint,
    IResponse,
    IResponseWithLoading,
    ISiteEquipmentSlim,
    ISiteEquipmentWithCoverages,
    IVideoStream,
    IViewCoverage,
    IrMap,
} from "App/Modules/Models";
import {
    ConfigurationGroupType,
    EquipmentClass,
    StreamSplitType,
    VideoStreamFunctions,
    AlarmZoneType,
    AlarmZoneTypeFunctions,
} from "App/Modules/Models";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditMotionDetectionFunctionalityComponentController implements IComponentController {
    public static $inject = [
        "$scope",
        "$element",
        "$attrs",
        "$timeout",
        "mmSvgService",
        "mmSitesRepository",
        "mmSiteFunctionalitiesRepository",
        "mmSiteEquipmentRepository",
        "mmCoverageCalculationsService",
        "irDefaultNamingService",
        "$translate",
        "irFeatureFlagService",
    ];

    public resolve: IFormContainerResolve<
        [IFunctionalityMotionDetection, string, boolean],
        ISiteEquipmentWithCoverages,
        IFunctionalityMotionDetection
    >;

    public subscription: Subscription;

    public isDataLoading = true;

    public functionality: IFunctionalityMotionDetection;

    public originalFunctionality: IFunctionalityMotionDetection;

    public tempAlarmZones: IAlarmZone[];

    public viewCoverage: IViewCoverage;

    public siteEquipment: ISiteEquipmentWithCoverages;

    public analogCameraEquipmentType = EquipmentClass.AnalogCamera;

    public map: IMap;

    public configurationGroups: IConfigurationGroupDetection[];

    public cameraSvgRoot: SVGSVGElement;

    public groundPlanePathElement: SVGSVGElement;

    public skewX: number;

    // New geometry stuff (TODO: Put in a struct?)
    public lowerDowntiltLimitDeg: number;

    public inverseStreamSplitPolygon: IPoint[];

    public showStreamSplitPolygon = false;

    public svgGroundPlanePathString: string;

    public svgGroundPlaneStrokeWidthPx: number;

    public splitWindowWidthPercentStr: string;

    public splitWindowHeightPercentStr: string;

    public splitWindowLeftPercentStr: string;

    public splitWindowTopPercentStr: string;

    public splitWindowWidthPx: number;

    public splitWindowHeightPx: number;

    public groundPlaneDefinition: number[][]; // [[x0,z0,x1,z1], ...]

    public humanSizeCalculator: HumanSizeService;

    public humanSize: any;

    public isDragging = false;

    public selectedAlarmZone: IAlarmZone;

    public newAlarmZone: IAlarmZone;

    public computerSiteEquipment: ISiteEquipmentSlim[];

    public videoStreams: IVideoStream[];

    public saveResponse: IResponseWithLoading<IFunctionalityMotionDetection>;

    public errorResponse: IResponse<IFunctionalityMotionDetection | IConfigurationGroupDetection[] | IMap>;

    public deleteResponse: IResponseWithLoading<void>;

    public alarmZoneTypes: IrMap<AlarmZoneType, string>;

    public analysisLimit = 0;

    public readonly loiteringTimes: number[] = [1, 2, 5, 10, 15];

    public readonly unattendedTimes: number[] = [1, 2, 5, 10, 15, 20, 30];

    public streamSplitRelativeCenter = 0;

    // UI states
    public addingAlarmZone = false;

    public isEditable: boolean;

    private matrixCalculationTimerPromise: any = null;

    private readonly humanSizeAdjustmentScrollStep: number = 0.1;

    private readonly humanSizeAdjustmentMin: number = 0.5;

    private readonly humanSizeAdjustmentMax: number = 2;

    constructor(
        private $scope: IScope,
        private $element: IAugmentedJQuery,
        private $attrs: IAttributes,
        private $timeout: ITimeoutService,
        private svgService: any,
        private sitesRepository: ISitesRepository,
        private siteFunctionalitiesRepository: ISiteFunctionalitiesRepository,
        private siteEquipmentRepository: ISiteEquipmentRepository,
        private coverageCalculationsService: ICoverageCalculationsService,
        private defaultNamingService: IDefaultNamingService,
        private readonly $translate: ng.translate.ITranslateService,
        private readonly featureFlagService: IFeatureFlagService,
    ) {}

    public $onInit() {
        this.siteEquipment = this.resolve.bindings;
        const [fn, siteId, isEditable] = this.resolve.formData;

        const map$ = this.sitesRepository.getMap(siteId);

        const configurationGroups$ = this.sitesRepository.getConfigurationGroups(
            siteId,
            ConfigurationGroupType.Detection,
        );

        const mdFunctionality$ = this.siteFunctionalitiesRepository.getFunctionality<IFunctionalityMotionDetection>(
            siteId,
            fn.id,
        );

        const computerSiteEquipment$ = this.sitesRepository.getSiteEquipments(siteId, EquipmentClass.Computer);

        let videoStreamEquipmentId = this.siteEquipment.id;
        if (this.siteEquipment.masterSiteEquipment) {
            videoStreamEquipmentId = this.siteEquipment.masterSiteEquipment.id;
        }

        this.updateGeometry();

        const videoStreams$ = this.siteEquipmentRepository.getVideoStreamsForSiteEquipment(
            siteId,
            videoStreamEquipmentId,
        );

        this.isEditable = isEditable;

        this.subscription = observableZip(
            map$,
            configurationGroups$,
            mdFunctionality$,
            computerSiteEquipment$,
            videoStreams$,
        )
            .pipe(
                finalize(() => {
                    this.isDataLoading = false;
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                ([map, configurationGroups, functionality, computerSiteEquipment, videoStreams]) => {
                    this.functionality = functionality.data;
                    this.originalFunctionality = cloneDeep(this.functionality);
                    this.viewCoverage = cloneDeep(this.functionality.viewCoverage);

                    this.configurationGroups = configurationGroups.data as IConfigurationGroupDetection[];
                    this.configurationGroups.forEach((cg) => this.defaultNamingService.nameConfigurationGroup(cg));

                    this.map = map.data;
                    this.computerSiteEquipment = computerSiteEquipment.data;
                    this.videoStreams = VideoStreamFunctions.combineVideoStreamLists(
                        videoStreams.data,
                        this.siteEquipment,
                    );
                    this.init(this.viewCoverage, this.functionality.videoStream);
                    this.setupWatches();
                    this.setupEvents();

                    // Convert to absolute coordinates
                    this.convertToAbsoluteCoordinates();

                    this.tempAlarmZones = cloneDeep(this.functionality.alarmZones);
                },
                (err: IResponse<IFunctionalityMotionDetection | IConfigurationGroupDetection[] | IMap>) => {
                    this.errorResponse = err;
                },
            );

        kendo.cultures["en-US"].numberFormat.percent.pattern = ["-n%", "n%"];

        this.alarmZoneTypes = AlarmZoneTypeFunctions.GetAlarmZoneTypeDictionary(this.$translate)
            .filter((item) => this.alarmZoneTypeFeatureFlagFilter.call(this, item.key))
            .sort((azt, bzt) => {
                // Sort select box items alphabetically
                if (azt.value < bzt.value) {
                    return -1;
                }
                if (azt.value > bzt.value) {
                    return 1;
                }
                return 0;
            });
    }

    public $onDestroy() {
        this.subscription.unsubscribe();
    }

    private alarmZoneTypeFeatureFlagFilter(alarmZoneType): boolean {
        switch (alarmZoneType) {
            case AlarmZoneType.ViolentBehaviorDetection:
                return this.featureFlagService.getFlag("preview-violent-behavior");
            case AlarmZoneType.FallDetection:
                return this.featureFlagService.getFlag("preview-fall-detection");
            case AlarmZoneType.UnattendedObjectDetection:
                return this.featureFlagService.getFlag("preview-unattended-object");
            default:
                return true;
        }
    }

    public addNewAlarmZonePoint(event: any) {
        const mousePosition: IPoint = {
            x: event.clientX,
            y: event.clientY,
        };
        const p = this.svgService.pointToSvgCoordinates(this.cameraSvgRoot, mousePosition);
        this.newAlarmZone.polygon.push({ x: p.x, y: p.y });
    }

    public addNewAlarmZone(): void {
        if (this.newAlarmZone && this.isZoneComplete()) {
            this.functionality.alarmZones.push(this.newAlarmZone);
            this.tempAlarmZones = cloneDeep(this.functionality.alarmZones);
            this.addingAlarmZone = false;
            this.selectedAlarmZone = null;
        }
        this.newAlarmZone = null;
    }

    public deleteAlarmZone(index: number) {
        this.functionality.alarmZones.splice(index, 1);
        this.tempAlarmZones = cloneDeep(this.functionality.alarmZones);
        this.selectedAlarmZone = null;
    }

    public editingAlarmZone(index: number, editing: boolean) {
        if (editing) {
            forEach(this.functionality.alarmZones, (alarmZone, i) => {
                if (i !== index) {
                    alarmZone.editing = false;
                }
            });
            this.selectedAlarmZone = this.functionality.alarmZones[index];
        } else {
            this.selectedAlarmZone = null;
        }
    }

    public humanSizeDragging() {
        this.updateHumanFigureSize();
    }

    public humanSizeDragStart() {
        this.isDragging = true;
    }

    public humanSizeDragStop() {
        this.isDragging = false;
    }

    private get humanSizeAdjustment(): number {
        return this.functionality.humanSizeAdjustment;
    }

    private set humanSizeAdjustment(value: number) {
        this.functionality.humanSizeAdjustment = Math.max(
            this.humanSizeAdjustmentMin,
            Math.min(value, this.humanSizeAdjustmentMax),
        );
        this.updateHumanFigureSize();
        this.updateAnalysisLimit();
    }

    public humanSizeScrolling(delta: number) {
        this.humanSizeAdjustment += this.fixFloatingPointError(delta * this.humanSizeAdjustmentScrollStep);
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public save() {
        const clonedFunctionality = cloneDeep(this.functionality);
        if (this.functionality.alarmZones.length > 0) {
            // Convert to relative coordinates
            this.convertToRelativeCoordinates(clonedFunctionality);
        }

        const patchObject = PatchDelta.difference(this.originalFunctionality, clonedFunctionality);

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.siteFunctionalitiesRepository.patchFunctionality(
                    this.resolve.formData[1],
                    this.originalFunctionality.id,
                    this.functionality.type,
                    patchObject,
                ),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (saveResponse: IResponseWithLoading<IFunctionalityMotionDetection>) => {
                        this.saveResponse = saveResponse;
                        if (!saveResponse.isLoading && saveResponse.data) {
                            this.resolve.options.onOk({
                                ...saveResponse.data,
                                viewCoverage: this.viewCoverage,
                            });
                        }
                    },
                    (errorResponse) => {
                        this.saveResponse = errorResponse;
                    },
                );
        } else if (this.viewCoverage.downwardTilt !== this.functionality.viewCoverage.downwardTilt) {
            this.resolve.options.onOk({
                ...this.functionality,
                viewCoverage: this.viewCoverage,
            });
        } else {
            this.cancel();
        }
    }

    public deleteFunctionality() {
        extendWithLoadingAndFail(
            this.siteFunctionalitiesRepository.deleteFunctionality(this.resolve.formData[1], this.functionality.id),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;
                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (errorResponse) => {
                    this.deleteResponse = errorResponse;
                },
            );
    }

    public isZoneComplete(): boolean {
        return this.newAlarmZone && this.newAlarmZone.polygon.length > 2;
    }

    public onAnalysisTypeChanged(alarmZone: IAlarmZone): void {
        if (
            (alarmZone.type === AlarmZoneType.LoiteringDetection ||
                alarmZone.type === AlarmZoneType.UnattendedObjectDetection) &&
            alarmZone.loiteringTimeMinutes === null
        ) {
            alarmZone.loiteringTimeMinutes = 5;
        }
    }

    private init(viewCoverage: IViewCoverage, videoStream: IVideoStream) {
        if (viewCoverage && viewCoverage.siteEquipment) {
            viewCoverage.siteEquipment.equipmentElevation = viewCoverage.siteEquipment.equipmentElevation || 5;

            viewCoverage.downwardTilt = viewCoverage.downwardTilt || 20;

            viewCoverage.siteEquipment.rotationOnMap = viewCoverage.siteEquipment.rotationOnMap || 0;

            this.skewX = 0; // Skew - visual, should not affect calculations
        }

        this.coverageCalculationsService.calculateMapCoverage(this.map, viewCoverage);

        this.cameraSvgRoot = document.querySelector("#cameraSvg");
        this.groundPlanePathElement = document.querySelector("#groundPlanePath");

        this.humanSizeCalculator = new HumanSizeService({
            camera: {
                realHeight: videoStream.resolution.resolutionY,
                realWidth: videoStream.resolution.resolutionX,
            },
            map: {
                realHeight: this.map.image.height,
                realWidth: this.map.image.width,
            },
        });

        // Position the human in the center of the current stream split
        const streamSplitPolygon = this.coverageCalculationsService.getStreamSplitPolygon(viewCoverage.streamSplitType);

        const camCoverageCenterX =
            0.5 * (streamSplitPolygon[0].x + streamSplitPolygon[1].x) * videoStream.images.latest.width;

        const camCoverageCenterY =
            0.5 * (streamSplitPolygon[0].y + streamSplitPolygon[2].y) * videoStream.images.latest.height;

        this.humanSize = {
            height: 50,
            width: 25,
            x: camCoverageCenterX,
            y: camCoverageCenterY,
        };

        this.streamSplitRelativeCenter = 0.5 * (streamSplitPolygon[0].x + streamSplitPolygon[1].x);

        this.createGroundPlaneDefinition();

        this.updateGeometry();
    }

    private updateGeometry() {
        if (!this.viewCoverage || !this.viewCoverage.horizontalFieldOfView || !this.viewCoverage.aspectRatio) {
            this.lowerDowntiltLimitDeg = 1;
            return;
        }

        /* eslint-disable @typescript-eslint/no-unused-vars */
        const [hFovRad, vFovRad] = this.coverageCalculationsService.getFieldOfViewsInRadians(this.viewCoverage);
        /* eslint-enable @typescript-eslint/no-unused-vars */

        // Update lowerDowntiltLimitDeg /based on current vfov, to make sure that
        // the horizon is noincluded in the image)
        if (this.viewCoverage.minDownwardTilt) {
            // If we already have it, use it
            this.lowerDowntiltLimitDeg = this.viewCoverage.minDownwardTilt;
        } else {
            // Nope, not available, recompute it!
            this.lowerDowntiltLimitDeg = this.coverageCalculationsService.getMinDownwardTilt(this.viewCoverage);
        }

        // Get a polygon masking out parts of the video stream not included in the stream split
        this.inverseStreamSplitPolygon = this.coverageCalculationsService.getInverseStreamSplitPolygon(
            this.viewCoverage.streamSplitType,
        );

        this.coverageCalculationsService.polygonToAbsolute(
            this.inverseStreamSplitPolygon,
            this.functionality.videoStream.images.latest,
        );

        this.showStreamSplitPolygon =
            this.viewCoverage.streamSplitType !== StreamSplitType.NoSplit &&
            (this.viewCoverage.streamSplitType as any) !== "NoSplit";

        // Update other stuff describing the active split view part
        const streamSplitPolygon = this.coverageCalculationsService.getStreamSplitPolygon(
            this.viewCoverage.streamSplitType,
        );

        const relativeWidth = streamSplitPolygon[1].x - streamSplitPolygon[0].x;
        const relativeHeight = streamSplitPolygon[2].y - streamSplitPolygon[0].y;

        this.splitWindowLeftPercentStr = `${streamSplitPolygon[0].x * 100}%`;
        this.splitWindowTopPercentStr = `${streamSplitPolygon[0].y * 100}%`;
        this.splitWindowWidthPercentStr = `${relativeWidth * 100}%`;
        this.splitWindowHeightPercentStr = `${relativeHeight * 100}%`;

        this.splitWindowWidthPx = relativeWidth * this.functionality.videoStream.images.latest.width;
        this.splitWindowHeightPx = relativeHeight * this.functionality.videoStream.images.latest.height;

        // Update the SVG path describing the ground plane
        this.updateGroundPlaneSvgPath();
    }

    // Get a rotation matrix represented as a 9-long vector, stored in row-major order (row by row)
    // Parameters:
    //   yaw:   Rotation around y (rotation on the map, in radians)
    //   pitch: Rotation around x (upward tilt, pitch = -downtilt, in radians)
    //   roll:  Rotation around z (image plane roration, in radians)
    private getRotationMatrix(yaw: number, pitch: number, roll: number): number[] {
        const M = new Array(9).fill(0);

        // The math below is derived using octace-online using the following matlab-style expressions:
        // (where c stands for 'cos' and s stands for 'sin')
        // cx = sym('cx'); cy = sym('cy'); cz = sym('cz')
        // sx = sym('sx'); sy = sym('sy'); sz = sym('sz')
        // z = sym(0); o = sym(1);
        // Rx = [o, 0, 0 ; 0, cx, -sx ; 0, sx, cx]
        // Ry = [cy, 0, sy ; 0, o, 0 ; -sy, 0, cy]
        // Rz = [cz, -sz, 0 ; sz, cz, 0 ; 0, 0, o]
        // M = Rz * Rx * Ry

        const sx = Math.sin(pitch);
        const cx = Math.cos(pitch);
        const sy = Math.sin(yaw);
        const cy = Math.cos(yaw);
        const sz = Math.sin(roll);
        const cz = Math.cos(roll);

        // Top row
        M[0] = cy * cz - sx * sy * sz;
        M[1] = -cx * sz;
        M[2] = cy * sx * sz + cz * sy;

        M[3] = cy * sz + cz * sx * sy;
        M[4] = cx * cz;
        M[5] = -cy * cz * sx + sy * sz;

        M[6] = -cx * sy;
        M[7] = sx;
        M[8] = cx * cy;

        return M;
    }

    // Return M * [x ; y ; z] as a 3-vector, where M is represented by a row-major vector of elements
    private matrixTransform(matrix: number[], x: number, y: number, z: number): number[] {
        const rx = matrix[0] * x + matrix[1] * y + matrix[2] * z;
        const ry = matrix[3] * x + matrix[4] * y + matrix[5] * z;
        const rz = matrix[6] * x + matrix[7] * y + matrix[8] * z;
        return [rx, ry, rz];
    }

    // Clip the 3D (x0,y0,z0)-(x1,y1,z1) from the 0 end such that it is strictly in front
    // of the clip plane z=clipZ. Returns new (x0,y0,z0) values that are restricted to within
    // the clip area. To completely clip a line, call this function twice, clipping from
    // each line end.
    private clipLine(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, clipZ: number): number[] {
        if (z0 < clipZ) {
            const k = (z1 - clipZ) / (z1 - z0);
            x0 = x1 - (x1 - x0) * k;
            y0 = y1 - (y1 - y0) * k;
            z0 = clipZ;
        }

        return [x0, y0, z0];
    }

    // Create the ground plane lines (defined in world coordinates)
    private createGroundPlaneDefinition() {
        // In the ground plane definition, x,y,z, are oriented according to a nomimal, non-rotated camera,
        // i.e. x points to the right, y points down, and z points away from the camera.

        // Define the grid geometry
        const nofHorizontalLines = 41;
        const nofVerticalLines = 41;
        const lineSpacing = 5; // Line spacing in meters in the real world
        const gridStartZ = -20; // Grid starts at this position in z (negative = behind the camera)

        // Compute some useful grid measurements
        const gridTotalWidth = (nofVerticalLines - 1) * lineSpacing;
        const gridTotalHeight = (nofHorizontalLines - 1) * lineSpacing;

        // Datatype for holding the original grid. On the form [[x0,z0,x1,z1], ...], where each inner
        // array defines a line in the xz plane, (x0,z0)-(x1,x1) (all lines assumed to be in the ground
        // plane, z = cameraHeight
        this.groundPlaneDefinition = [];

        // Place the "horizontal lines" (lines oriented along the x axis)
        for (let zi = 0; zi < nofHorizontalLines; zi++) {
            const wx0 = -0.5 * gridTotalWidth;
            const wz0 = gridStartZ + zi * lineSpacing;
            const wx1 = 0.5 * gridTotalWidth;
            const wz1 = wz0;
            this.groundPlaneDefinition.push([wx0, wz0, wx1, wz1]);
        }

        // Place the "vertical lines" (lines oriented along the z axis)
        for (let xi = 0; xi < nofVerticalLines; xi++) {
            const wx0 = -0.5 * gridTotalWidth + xi * lineSpacing;
            const wz0 = gridStartZ;
            const wx1 = wx0;
            const wz1 = gridStartZ + gridTotalHeight;
            this.groundPlaneDefinition.push([wx0, wz0, wx1, wz1]);
        }
    }

    // Create a ground plane SVG path based on the current camera rotation and position.
    // Also update the stroke width to make it look as good as possible.
    private updateGroundPlaneSvgPath() {
        // Compute horizontal and vertical field of view
        const [hFovRad, vFovRad] = this.coverageCalculationsService.getFieldOfViewsInRadians(this.viewCoverage);

        // Compute the rotation matrix
        const yaw = (this.skewX * Math.PI) / 180;
        const pitch = (this.viewCoverage.downwardTilt * Math.PI) / 180;
        const roll = 0; // Later, we could support a camera rotation (around its own axis) here
        const M = this.getRotationMatrix(yaw, pitch, roll);

        // Define the camera matrix elements (mapping points in meters to normalized coordinats, range 0..1)
        const fx = (0.5 * this.splitWindowWidthPx) / Math.tan(0.5 * hFovRad);
        const fy = (0.5 * this.splitWindowHeightPx) / Math.tan(0.5 * vFovRad);
        const px = 0.5 * this.splitWindowWidthPx;
        const py = 0.5 * this.splitWindowHeightPx;

        // Shared stuff for all points: height of the camera (in meters) and clip plane
        // location(meters in front of the camera)
        const height = this.viewCoverage.siteEquipment.equipmentElevation;
        const clipZ = 1;

        // Transform the ground plane points to view coordinates and create SVG path strings on the fly
        const pathStrings: string[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const gd of this.groundPlaneDefinition) {
            let wx0 = gd[0];
            let wy0 = height;
            let wz0 = gd[1];
            let wx1 = gd[2];
            let wy1 = height;
            let wz1 = gd[3];

            // Rotate points to be aligned with the camera
            [wx0, wy0, wz0] = this.matrixTransform(M, wx0, wy0, wz0);
            [wx1, wy1, wz1] = this.matrixTransform(M, wx1, wy1, wz1);

            // Don't draw line that are strictly behind the clip plane
            if (wz0 < clipZ && wz1 < clipZ) {
                continue;
            }

            // Clip the line such that only the part in front of the clip plane is kept
            [wx0, wy0, wz0] = this.clipLine(wx0, wy0, wz0, wx1, wy1, wz1, clipZ);
            [wx1, wy1, wz1] = this.clipLine(wx1, wy1, wz1, wx0, wy0, wz0, clipZ);

            // Project to view space (active split window pixel coordinates)
            const cx0 = (fx * wx0) / wz0 + px;
            const cy0 = (fy * wy0) / wz0 + py;

            const cx1 = (fx * wx1) / wz1 + px;
            const cy1 = (fy * wy1) / wz1 + py;

            // If entirely outside the (split window of the) camera image, ignore it
            if (
                (cx0 < 0 && cx1 < 0) ||
                (cx0 > this.splitWindowWidthPx && cx1 > this.splitWindowWidthPx) ||
                (cy0 < 0 && cy1 < 0) ||
                (cy0 > this.splitWindowHeightPx && cy1 > this.splitWindowHeightPx)
            ) {
                continue;
            }

            // Ok, at least parts of this line is within the camera image. Make a svg path string for it
            const pathString = `M ${Math.round(cx0)} ${Math.round(cy0)} L ${Math.round(cx1)} ${Math.round(cy1)}`;
            pathStrings.push(pathString);
        }

        // Combine to a single string and update the path element
        const svgGroundPlanePathString = pathStrings.join(" ");
        this.groundPlanePathElement.setAttribute("d", svgGroundPlanePathString);

        // Workaround to make the stroke width look good on all resolutions. Since the stroke width is expressed in
        // svg - internal coordinates (which are pixels), but shown in a frame that may be downsampled, we need a
        // larger stroke width for very large video stream images.
        const nominalStrokeWidth = 1.5;
        if (this.functionality.videoStream.images.latest.width > 640) {
            this.svgGroundPlaneStrokeWidthPx =
                nominalStrokeWidth * (this.functionality.videoStream.images.latest.width / 640);
        } else {
            this.svgGroundPlaneStrokeWidthPx = nominalStrokeWidth;
        }
    }

    private setupWatches() {
        this.$scope.$watch("$ctrl.addingAlarmZone", (newValue) => {
            if (newValue) {
                this.selectedAlarmZone = null;
                this.newAlarmZone = {
                    configurationGroup: this.configurationGroups[0],
                    editing: false,
                    id: undefined, // todo: Erik L: Changed from "void 0", what is intent here?
                    polygon: [],
                    type: AlarmZoneType.MotionDetection,
                    loiteringTimeMinutes: null,
                    externalIntegrationId: null,
                };
            } else {
                this.newAlarmZone = null;
            }
        });

        this.$scope.$watch("$ctrl.viewCoverage.downwardTilt", () => {
            this.calculateFootprint();
            this.updateGeometry();
        });

        this.$scope.$watch("$ctrl.skewX", () => {
            this.updateGeometry();
        });

        this.$scope.$watch("$ctrl.functionality.videoStream", () => {
            this.calculateAlarmZones();
        });

        this.$scope.$watch("$ctrl.functionality.humanSizeAdjustment", () => {
            // Below delegates to getter/setter. Presumably does not get compiled away despite looking like noop.
            // eslint-disable-next-line no-self-assign
            this.humanSizeAdjustment = this.humanSizeAdjustment;
        });
    }

    private setupEvents() {
        this.$element.find("#cameraSvg").bind("mousewheel", (jqueryEvent: any) => {
            if (this.isDragging) {
                jqueryEvent.preventDefault();
                const args = {
                    delta: jqueryEvent.originalEvent.deltaY as number,
                };
                const direction = Math.sign(-args.delta);
                this.humanSizeScrolling(direction);
            }
        });
    }

    private calculateAlarmZones() {
        if (this.originalFunctionality.videoStream.id !== this.functionality.videoStream.id) {
            const cloneFunctionality = cloneDeep(this.functionality);
            const originalImage = this.originalFunctionality.videoStream.images.latest;

            cloneFunctionality.alarmZones.forEach((az) => {
                this.coverageCalculationsService.polygonToNormalizedForResolution(
                    az.polygon,
                    originalImage.width,
                    originalImage.height,
                );
            });

            const newImage = cloneFunctionality.videoStream.images.latest;
            cloneFunctionality.alarmZones.forEach((az) => {
                this.coverageCalculationsService.polygonToAbsoluteForResolution(
                    az.polygon,
                    newImage.width,
                    newImage.height,
                );
            });

            this.functionality.alarmZones = cloneFunctionality.alarmZones;
        } else {
            this.functionality.alarmZones = this.tempAlarmZones;
        }
        // Finally remove alarm zones blocked by feature flagging
        this.functionality.alarmZones = this.functionality.alarmZones.filter((item) =>
            this.alarmZoneTypeFeatureFlagFilter.call(this, item.type),
        );
    }

    private calculateFootprint() {
        // Human size calculator expects inputs in relative coordinates (this is in accordance
        // with the intended design direction - use relative coordinates as long as possible)
        this.humanSizeCalculator.setMapCameraPosition(this.viewCoverage.siteEquipment.positionOnMap);

        this.humanSizeCalculator.setMapScalePoints(this.map.scaleCoordinates);
        this.humanSizeCalculator.setMapScaleValue(this.map.scaleValue);

        // The map footprint needs to be recomputed. The coverage calculation service wants absolute
        // coordinates as input (for historical reasons - should be adapted to input normalized coordinates).
        // For now, temporarily convert to absolute coordinates before computing the coverage, then back to relative
        const tempMap = cloneDeep(this.map);
        const tempViewCoverage = cloneDeep(this.viewCoverage);

        this.coverageCalculationsService.polygonToAbsolute(tempViewCoverage.mapCoveragePolygon, this.map.image);
        this.coverageCalculationsService.polygonToAbsolute(tempMap.scaleCoordinates, this.map.image);
        this.coverageCalculationsService.toAbsolute(tempViewCoverage.siteEquipment.positionOnMap, this.map.image);

        this.coverageCalculationsService.calculateMapCoverage(tempMap, tempViewCoverage);

        this.coverageCalculationsService.polygonToNormalized(tempViewCoverage.mapCoveragePolygon, this.map.image);

        this.humanSizeCalculator.setMapFootprint(tempViewCoverage.mapCoveragePolygon);
        this.humanSizeCalculator.setCameraCoveragePolygon(tempViewCoverage.cameraCoveragePolygon);

        if (this.matrixCalculationTimerPromise) {
            this.$timeout.cancel(this.matrixCalculationTimerPromise);
        }

        this.matrixCalculationTimerPromise = this.$timeout(() => {
            this.humanSizeCalculator.calculateWidthMap();
            this.$scope.$apply(() => this.updateHumanFigureSize());
            this.$scope.$apply(() => this.updateAnalysisLimit());
        }, 2000);
    }

    private convertToRelativeCoordinates(
        clonedFunctionality: IFunctionalityMotionDetection,
    ): IFunctionalityMotionDetection {
        const image = this.functionality.videoStream.images.latest;

        clonedFunctionality.alarmZones.forEach((az) => {
            this.coverageCalculationsService.polygonToNormalizedForResolution(az.polygon, image.width, image.height);
        });

        return clonedFunctionality;
    }

    private convertToAbsoluteCoordinates() {
        const image = this.functionality.videoStream.images.latest;
        this.functionality.alarmZones.forEach((az) => {
            this.coverageCalculationsService.polygonToAbsoluteForResolution(az.polygon, image.width, image.height);
        });
    }

    private updateHumanFigureSize() {
        const center = {
            // Already translated in SVG to be bottom center
            x: this.humanSize.x / this.functionality.videoStream.images.latest.width,
            y: this.humanSize.y / this.functionality.videoStream.images.latest.height,
        };

        const pixelsPerMeter =
            this.humanSizeCalculator.getPixelsPerMeter(center.x, center.y) *
            this.functionality.videoStream.images.latest.width *
            this.humanSizeAdjustment;

        // Number.isFinite is preferred but might not always give same values, so left for now. Do change when testing.
        // eslint-disable-next-line no-restricted-globals
        if (!isFinite(pixelsPerMeter) || pixelsPerMeter === 0) {
            return;
        }

        const grownManHeightMeters = 1.8;

        const grownManHeightPixels = grownManHeightMeters * pixelsPerMeter;
        const grownManWidthPixels = grownManHeightPixels / 2;

        this.humanSize.height = 1 * grownManHeightPixels;
        this.humanSize.width = 1 * grownManWidthPixels;
    }

    private updateAnalysisLimit() {
        const heightInPixels = this.functionality.videoStream.images.latest.height;
        const pixelsPerMeterLimit = 20.0 / 1.8; // = 11.1 pixels per meter, coresponds to 20 px on a grown man of 1.8m

        for (let y = 0; y < heightInPixels; y++) {
            const pixelsPerMeter =
                this.humanSizeCalculator.getPixelsPerMeter(this.streamSplitRelativeCenter, y / heightInPixels) *
                this.functionality.videoStream.images.latest.width *
                this.humanSizeAdjustment;

            if (pixelsPerMeter > pixelsPerMeterLimit) {
                this.analysisLimit = y;
                return;
            }
        }
    }

    private fixFloatingPointError(val: number) {
        return Math.round(val * 100) / 100;
    }
}
